/**
 * useAccountTestCounts
 *
 * Fans out `getExperimentsList` across every active property to power the
 * per-row "N tests" chip on Sites Directory. Without this, the dashboard
 * can't surface "this site has running tests" — a feedback item the agents
 * (Growth, Design) both flagged.
 *
 * Why fan-out: there's no account-level batch endpoint. React Query caches
 * each result for 5 minutes and dedupes if other parts of the app (like the
 * Experiments page) request the same property's tests.
 */

import { useQueries } from '@tanstack/react-query'
import { useMemo } from 'react'
import { useAuth } from '@/lib/contexts/AuthContext'
import { getExperimentsList } from '@/lib/api/api'

interface UseAccountTestCountsInput {
  properties: Array<{ propertyId: string; isDemo?: boolean; archived?: boolean }>
}

interface SiteTestCounts {
  /** Currently running (status === 'active') experiments. */
  active: number
  /** Tests that have reached statistical significance (≥95). */
  readyToCall: number
  /** All non-archived tests. */
  total: number
}

interface UseAccountTestCountsResult {
  /** Per-property test counts, keyed by propertyId. */
  byPropertyId: Record<string, SiteTestCounts>
  /** Aggregate active tests across all sites. */
  totalActive: number
  /** Aggregate ready-to-call across all sites. */
  totalReadyToCall: number
  isLoading: boolean
}

const SIG_THRESHOLD = 95

export function useAccountTestCounts({
  properties,
}: UseAccountTestCountsInput): UseAccountTestCountsResult {
  const { token } = useAuth()

  const targetIds = useMemo(
    () =>
      properties
        .filter((p) => !p.isDemo && !p.archived)
        .map((p) => p.propertyId),
    [properties]
  )

  const results = useQueries({
    queries: targetIds.map((propertyId) => ({
      // Separate key from useExperiments — that hook transforms the response into
      // a different shape (variants array vs Record), so sharing the key would
      // corrupt whichever query populates the cache first.
      queryKey: ['experiments-raw', propertyId],
      queryFn: async (): Promise<any[] | null> => {
        if (!token) return null
        try {
          const data = await getExperimentsList(propertyId, token)
          return Array.isArray(data) ? data : []
        } catch {
          return null
        }
      },
      enabled: !!token && !!propertyId,
      staleTime: 5 * 60 * 1000,
      retry: false,
    })),
  })

  return useMemo<UseAccountTestCountsResult>(() => {
    const byPropertyId: Record<string, SiteTestCounts> = {}
    let totalActive = 0
    let totalReadyToCall = 0
    let anyLoading = false

    targetIds.forEach((propertyId, i) => {
      const q = results[i]
      if (q?.isLoading) anyLoading = true
      const tests = q?.data
      if (!Array.isArray(tests)) return

      let active = 0
      let readyToCall = 0
      let total = 0
      for (const t of tests) {
        if (t?.status === 'archived') continue
        total += 1
        if (t?.status === 'active') active += 1
        if ((t?.statisticalSignificance ?? 0) >= SIG_THRESHOLD) readyToCall += 1
      }

      byPropertyId[propertyId] = { active, readyToCall, total }
      totalActive += active
      totalReadyToCall += readyToCall
    })

    return {
      byPropertyId,
      totalActive,
      totalReadyToCall,
      isLoading: anyLoading,
    }
  }, [results, targetIds])
}
